/** Sprint 5-d PR-4 · 對話 timeline（user 右 bubble / assistant 左 markdown / tool_use·tool_result 摺疊）。 */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import DownloadIcon from '@mui/icons-material/Download';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { BillAttachmentItem, TimelineItem } from '../types';
import { getI18nValue } from '../../../../utils/i18nHelper';
import ChatWorkingStatus, { WorkPhase } from './ChatWorkingStatus';

interface Props {
  items: TimelineItem[];
  streaming: boolean;
  workPhase?: WorkPhase | null;      // §5.7 作業中階段（streaming 期間）
  workTool?: string;                 // querying 時的工具代碼（顯示名稱於渲染時才查）
  toolLabel?: (code?: string) => string | undefined;        // 工具代碼→中文名（渲染時查·避免晚載顯代碼）
  toolRequiresConfirm?: (code?: string) => boolean;         // 工具是否需確認（渲染時查）
  onReopenCard?: (it: TimelineItem) => void;   // §5.8 「再次開啟」
  onConfirm?: (it: TimelineItem) => void;      // E5 §5.1 確認卡
  onCancel?: (it: TimelineItem) => void;
  confirmBusy?: number | null;                 // 進行中的 actionId（按鈕轉圈）
  onDownloadBillAttachment?: (billId: number, att: BillAttachmentItem) => void;  // my_bills §3.3
}

const MessageList: React.FC<Props> = ({
  items, streaming, workPhase, workTool, toolLabel, toolRequiresConfirm,
  onReopenCard, onConfirm, onCancel, confirmBusy, onDownloadBillAttachment,
}) => {
  const { t, i18n } = useTranslation();
  const fmtSize = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB`
    : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [items, streaming, workPhase]);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, md: 2 }, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.length === 0 && (
        <Typography color="text.secondary" sx={{ m: 'auto' }}>
          {t('chat.emptyHint', '開始一段新對話。可從上方工具列選功能，或直接輸入。')}
        </Typography>
      )}
      {items.map((it) => {
        if (it.kind === 'user') {
          return (
            <Box key={it.key} sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
              <Paper sx={{ p: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{it.content}</Typography>
              </Paper>
            </Box>
          );
        }
        if (it.kind === 'assistant') {
          return (
            <Box key={it.key} sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                {it.content
                  ? <Box sx={{ '& p': { m: 0.5, mt: 0 }, '& pre': { overflowX: 'auto' } }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{it.content}</ReactMarkdown>
                    </Box>
                  : (it.pending && <CircularProgress size={16} />)}
              </Paper>
            </Box>
          );
        }
        if (it.kind === 'navigate_card') {
          const fname = getI18nValue(it.navFuncName as never, i18n.language) || it.navFuncCode || '';
          return (
            <Box key={it.key} sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <OpenInNewIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {t('chat.card.opened', '已開啟「{{name}}」', { name: fname })}
                </Typography>
                <Button size="small" onClick={() => onReopenCard?.(it)}>
                  {t('chat.card.reopen', '再次開啟')}
                </Button>
              </Paper>
            </Box>
          );
        }
        if (it.kind === 'contact_card') {
          // §12 聯絡人撥號卡片：react-markdown 會拔掉 tel: 連結·故以結構化卡片 + 真實 <a> 撥打鈕呈現
          const c = it.contact || {};
          const phoneLabel = (k?: string) => (
            k === 'mobile' ? t('hrResume.phone', '手機')
              : k === 'home' ? t('hrResume.phoneHome', '住家電話')
              : k === 'emergency' ? t('hrResume.emgName', '緊急聯絡人')
              : (k || ''));
          const sub = [c.department, c.job_title].filter(Boolean).join(' · ');
          return (
            <Box key={it.key} sx={{ alignSelf: 'flex-start', maxWidth: '85%', width: '100%' }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ContactPhoneIcon fontSize="small" color="primary" />
                  <Box>
                    <Typography variant="subtitle2">{c.name || ''}</Typography>
                    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
                  </Box>
                </Box>
                {(c.phones || []).map((p, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5,
                    borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {phoneLabel(p.label_key)}{p.name ? `（${p.name}）` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{p.number}</Typography>
                    </Box>
                    {p.tel && (
                      <Button size="small" variant="outlined" component="a" href={`tel:${p.tel}`}
                        startIcon={<PhoneIcon sx={{ fontSize: 16 }} />}>
                        {t('hrResume.callAction', '撥打')}
                      </Button>
                    )}
                  </Box>
                ))}
                {c.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5,
                    borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: '0 0 auto' }}>
                      {t('hrResume.email', 'Email')}
                    </Typography>
                    <Typography variant="body2" component="a" href={`mailto:${c.email}`}
                      sx={{ wordBreak: 'break-all' }}>{c.email}</Typography>
                  </Box>
                )}
                {(c.phones || []).length === 0 && !c.email && (
                  <Typography variant="caption" color="text.secondary">
                    {t('hrResume.noData', '尚無資料')}
                  </Typography>
                )}
              </Paper>
            </Box>
          );
        }
        if (it.kind === 'bill_attachments_card') {
          // my_bills §3.3 帳單附件下載卡片：下載走既有端點（帶登入憑證）·檔案內容不進對話
          const ba = it.billAttachments;
          const atts = ba?.attachments || [];
          return (
            <Box key={it.key} sx={{ alignSelf: 'flex-start', maxWidth: '85%', width: '100%' }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AttachFileIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2">
                    {t('platformBilling.attachments', '附件')}
                    {ba?.bill_no ? `・${ba.bill_no}` : ''}
                  </Typography>
                </Box>
                {atts.map((att) => (
                  <Box key={att.att_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5,
                    borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{att.filename}</Typography>
                      <Typography variant="caption" color="text.secondary">{fmtSize(att.size)}</Typography>
                    </Box>
                    <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                      onClick={() => ba && onDownloadBillAttachment?.(ba.bill_id, att)}>
                      {t('platformBilling.attDownload', '下載')}
                    </Button>
                  </Box>
                ))}
                {atts.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('platformBilling.noAttachments', '尚無附件')}
                  </Typography>
                )}
              </Paper>
            </Box>
          );
        }
        if (it.kind === 'confirm_card') {
          const st = it.confirmStatus || 'pending';
          const expired = st === 'expired';
          const busy = confirmBusy === it.actionId;
          return (
            <Box key={it.key} sx={{ alignSelf: 'flex-start', maxWidth: '85%', width: '100%' }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: 'primary.main' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{it.confirmSummary}</Typography>
                {st === 'pending' ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="small" variant="contained" disabled={busy || expired}
                      onClick={() => onConfirm?.(it)}>{t('chat.confirm.ok', '確認')}</Button>
                    <Button size="small" disabled={busy} onClick={() => onCancel?.(it)}>
                      {t('chat.confirm.cancel', '取消')}</Button>
                    <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                      {t('chat.confirm.expiresHint', '10 分鐘內有效')}</Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" color={st === 'executed' ? 'success.main' : 'text.secondary'}>
                    {st === 'executed' ? t('chat.confirm.done', '✓ 已完成')
                      : st === 'cancelled' ? t('chat.confirm.cancelled', '已取消')
                      : st === 'expired' ? t('chat.confirm.expired', '已過期')
                      : t('chat.confirm.failed', '失敗')}
                  </Typography>
                )}
              </Paper>
            </Box>
          );
        }
        if (it.kind === 'tool_use') {
          // E5.1 §2.1：改為一行可讀狀態（主題色·不寫死·不顯原始 JSON；完整參數存 ee_ai_chat_tool_calls）
          // 顯示名稱 / 需確認於渲染時依最新 categories 查（工具清單晚到時自動更新為中文名）
          const label = (toolLabel ? toolLabel(it.toolCode) : undefined) || it.toolName || it.toolCode;
          const awaiting = toolRequiresConfirm ? toolRequiresConfirm(it.toolCode) : it.pending;
          return (
            <Box key={it.key} sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 0.5, px: 1 }}>
              <BuildIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {t('chat.toolUsed', '已使用')}：{label}
                {awaiting ? `（${t('chat.confirm.awaiting', '等待您確認')}）` : ''}
              </Typography>
            </Box>
          );
        }
        // tool_result：E5.1 不再顯示原始 JSON（證據已存資料庫·管理員可查）
        return null;
      })}
      {streaming && workPhase && (
        <ChatWorkingStatus phase={workPhase} toolLabel={toolLabel ? toolLabel(workTool) : workTool} />
      )}
      <div ref={endRef} />
    </Box>
  );
};

export default MessageList;
